import { RecipeIngredient, ValidationError } from '../../types/recipe';

const CANNABINOID_TYPES = [
  'THC',
  'CBD',
  'CBN',
  'CBG',
  'CBC',
  'THCV',
  'Delta-8 THC',
] as const;

interface IngredientRowProps {
  ingredient: RecipeIngredient;
  index: number;
  onChange: (index: number, ingredient: RecipeIngredient) => void;
  onRemove: (index: number) => void;
  errors: ValidationError[];
}

function getErrorForField(
  errors: ValidationError[],
  field: string,
  index: number
): ValidationError | undefined {
  return errors.find(
    (e) => e.field === field && e.ingredientIndex === index
  );
}

function errorId(index: number, field: string): string {
  return `ingredient-${index}-${field}-error`;
}

export function IngredientRow({
  ingredient,
  index,
  onChange,
  onRemove,
  errors,
}: IngredientRowProps) {
  function handleFieldChange(field: keyof RecipeIngredient, value: string | number | boolean) {
    onChange(index, { ...ingredient, [field]: value });
  }

  function handleInfusedToggle(checked: boolean) {
    if (checked) {
      onChange(index, {
        ...ingredient,
        isCannabisInfused: true,
        cannabinoidType: ingredient.cannabinoidType || 'THC',
        cannabinoidPercentage: ingredient.cannabinoidPercentage,
        materialWeight: ingredient.materialWeight,
        decarboxylationEfficiency: ingredient.decarboxylationEfficiency ?? 0.88,
        extractionEfficiency: ingredient.extractionEfficiency ?? 0.70,
      });
    } else {
      onChange(index, {
        ...ingredient,
        isCannabisInfused: false,
        cannabinoidType: undefined,
        cannabinoidPercentage: undefined,
        materialWeight: undefined,
        decarboxylationEfficiency: undefined,
        extractionEfficiency: undefined,
      });
    }
  }

  const nameError = getErrorForField(errors, 'name', index);
  const amountError = getErrorForField(errors, 'amount', index);
  const unitError = getErrorForField(errors, 'unit', index);
  const cannabinoidTypeError = getErrorForField(errors, 'cannabinoidType', index);
  const cannabinoidPercentageError = getErrorForField(errors, 'cannabinoidPercentage', index);
  const materialWeightError = getErrorForField(errors, 'materialWeight', index);
  const decarboxylationError = getErrorForField(errors, 'decarboxylationEfficiency', index);
  const extractionError = getErrorForField(errors, 'extractionEfficiency', index);

  return (
    <div className="ingredient-row" role="group" aria-label={`Ingredient ${index + 1}`}>
      <div className="ingredient-row-fields">
        <div className="form-group">
          <label htmlFor={`ingredient-${index}-name`}>Name</label>
          <input
            id={`ingredient-${index}-name`}
            type="text"
            value={ingredient.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            maxLength={100}
            aria-describedby={nameError ? errorId(index, 'name') : undefined}
            aria-invalid={!!nameError}
          />
          {nameError && (
            <span id={errorId(index, 'name')} className="field-error" role="alert">
              {nameError.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={`ingredient-${index}-amount`}>Amount</label>
          <input
            id={`ingredient-${index}-amount`}
            type="number"
            value={ingredient.amount || ''}
            onChange={(e) =>
              handleFieldChange('amount', e.target.value === '' ? 0 : parseFloat(e.target.value))
            }
            min={0.01}
            max={999999.99}
            step="any"
            aria-describedby={amountError ? errorId(index, 'amount') : undefined}
            aria-invalid={!!amountError}
          />
          {amountError && (
            <span id={errorId(index, 'amount')} className="field-error" role="alert">
              {amountError.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={`ingredient-${index}-unit`}>Unit</label>
          <input
            id={`ingredient-${index}-unit`}
            type="text"
            value={ingredient.unit}
            onChange={(e) => handleFieldChange('unit', e.target.value)}
            aria-describedby={unitError ? errorId(index, 'unit') : undefined}
            aria-invalid={!!unitError}
          />
          {unitError && (
            <span id={errorId(index, 'unit')} className="field-error" role="alert">
              {unitError.message}
            </span>
          )}
        </div>

        <button
          type="button"
          className="ingredient-remove-button"
          onClick={() => onRemove(index)}
          aria-label={`Remove ingredient ${index + 1}`}
        >
          Remove
        </button>
      </div>

      <div className="ingredient-infused-toggle">
        <label>
          <input
            type="checkbox"
            checked={ingredient.isCannabisInfused}
            onChange={(e) => handleInfusedToggle(e.target.checked)}
          />
          Cannabis Infused
        </label>
      </div>

      {ingredient.isCannabisInfused && (
        <div className="ingredient-infused-fields">
          <div className="form-group">
            <label htmlFor={`ingredient-${index}-cannabinoidType`}>Cannabinoid Type</label>
            <select
              id={`ingredient-${index}-cannabinoidType`}
              value={ingredient.cannabinoidType || ''}
              onChange={(e) => handleFieldChange('cannabinoidType', e.target.value)}
              aria-describedby={
                cannabinoidTypeError ? errorId(index, 'cannabinoidType') : undefined
              }
              aria-invalid={!!cannabinoidTypeError}
            >
              <option value="" disabled>
                Select type
              </option>
              {CANNABINOID_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {cannabinoidTypeError && (
              <span
                id={errorId(index, 'cannabinoidType')}
                className="field-error"
                role="alert"
              >
                {cannabinoidTypeError.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor={`ingredient-${index}-cannabinoidPercentage`}>
              Cannabinoid Percentage (%)
            </label>
            <input
              id={`ingredient-${index}-cannabinoidPercentage`}
              type="number"
              value={ingredient.cannabinoidPercentage ?? ''}
              onChange={(e) =>
                handleFieldChange(
                  'cannabinoidPercentage',
                  e.target.value === '' ? 0 : parseFloat(e.target.value)
                )
              }
              min={0.01}
              max={100}
              step="any"
              aria-describedby={
                cannabinoidPercentageError
                  ? errorId(index, 'cannabinoidPercentage')
                  : undefined
              }
              aria-invalid={!!cannabinoidPercentageError}
            />
            {cannabinoidPercentageError && (
              <span
                id={errorId(index, 'cannabinoidPercentage')}
                className="field-error"
                role="alert"
              >
                {cannabinoidPercentageError.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor={`ingredient-${index}-materialWeight`}>
              Material Weight (g)
            </label>
            <input
              id={`ingredient-${index}-materialWeight`}
              type="number"
              value={ingredient.materialWeight ?? ''}
              onChange={(e) =>
                handleFieldChange(
                  'materialWeight',
                  e.target.value === '' ? 0 : parseFloat(e.target.value)
                )
              }
              min={0.01}
              max={10000}
              step="any"
              aria-describedby={
                materialWeightError ? errorId(index, 'materialWeight') : undefined
              }
              aria-invalid={!!materialWeightError}
            />
            {materialWeightError && (
              <span
                id={errorId(index, 'materialWeight')}
                className="field-error"
                role="alert"
              >
                {materialWeightError.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor={`ingredient-${index}-decarboxylationEfficiency`}>
              Decarboxylation Efficiency (%)
            </label>
            <input
              id={`ingredient-${index}-decarboxylationEfficiency`}
              type="number"
              value={
                ingredient.decarboxylationEfficiency !== undefined
                  ? Math.round(ingredient.decarboxylationEfficiency * 100)
                  : ''
              }
              onChange={(e) =>
                handleFieldChange(
                  'decarboxylationEfficiency',
                  e.target.value === '' ? 0 : parseFloat(e.target.value) / 100
                )
              }
              min={1}
              max={100}
              step="any"
              aria-describedby={
                decarboxylationError
                  ? errorId(index, 'decarboxylationEfficiency')
                  : undefined
              }
              aria-invalid={!!decarboxylationError}
            />
            {decarboxylationError && (
              <span
                id={errorId(index, 'decarboxylationEfficiency')}
                className="field-error"
                role="alert"
              >
                {decarboxylationError.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor={`ingredient-${index}-extractionEfficiency`}>
              Extraction Efficiency (%)
            </label>
            <input
              id={`ingredient-${index}-extractionEfficiency`}
              type="number"
              value={
                ingredient.extractionEfficiency !== undefined
                  ? Math.round(ingredient.extractionEfficiency * 100)
                  : ''
              }
              onChange={(e) =>
                handleFieldChange(
                  'extractionEfficiency',
                  e.target.value === '' ? 0 : parseFloat(e.target.value) / 100
                )
              }
              min={1}
              max={100}
              step="any"
              aria-describedby={
                extractionError ? errorId(index, 'extractionEfficiency') : undefined
              }
              aria-invalid={!!extractionError}
            />
            {extractionError && (
              <span
                id={errorId(index, 'extractionEfficiency')}
                className="field-error"
                role="alert"
              >
                {extractionError.message}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
